/**
 * @fileoverview Хуки для работы с камерой и API запросами.
 * Обеспечивают проверку разрешений камеры и обертки над API функциями.
 * 
 * @since 1.0.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryParams } from '../../../utils/hooks/useQueryParams';
import { api, GetFileUrlPayload } from '../../api/api';

/**
 * Хук для проверки разрешений камеры.
 * 
 * Проверяет доступность камеры через getUserMedia API.
 * Автоматически останавливает полученный поток для сохранения ресурсов.
 * 
 * @returns Состояние разрешения камеры:
 * - `true` - разрешение получено
 * - `false` - разрешение отклонено
 * - `null` - проверка в процессе
 * 
 * @remarks
 * Проверка выполняется однократно при монтировании хука.
 * Полученный медиа-поток немедленно останавливается.
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * function CameraComponent() {
 *   const hasPermission = useCameraPermission();
 * 
 *   if (hasPermission === null) return <div>Проверка...</div>;
 *   if (!hasPermission) return <div>Нет доступа к камере</div>;
 *   return <Webcam />;
 * }
 * ```
 */
export const useCameraPermission = () => {
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null)

  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })

        setCameraPermission(true)

        stream.getTracks().forEach((track) => track.stop())
      } catch (error) {
        setCameraPermission(false)
      }
    }

    // Check camera permission only if it hasn't been checked before
    if (cameraPermission === null) {
      checkCameraPermission()
    }
  }, [cameraPermission])
  
  return cameraPermission
}

/**
 * Хук для работы с API запросами камеры и загрузкой файлов.
 * 
 * Предоставляет мемоизированные обертки над API функциями
 * для работы с файлами и параметрами операции.
 * 
 * @returns Объект с API функциями и параметрами:
 * - `min` - минимальное количество фотографий
 * - `max` - максимальное количество фотографий
 * - `getFileUrl` - получение pre-signed URL для загрузки
 * - `uploadFile` - загрузка файла в S3
 * - `approveDoerPhotos` - подтверждение успешной загрузки
 * 
 * @remarks
 * **Параметры операции:**
 * - Получает min_size и max_size из URL параметров
 * - Использует значения по умолчанию (1-10) если не указаны
 * 
 * **Оптимизация:**
 * - Все функции мемоизированы через useCallback
 * - Результат мемоизирован через useMemo
 * - Предотвращает необоснованные перерендеры
 * 
 * @since 1.0.0
 * 
 * @example
 * ```tsx
 * function WebAppCam() {
 *   const { min, max, getFileUrl, uploadFile, approveDoerPhotos } = useCameraRequest();
 * 
 *   const handleUpload = async () => {
 *     // Получаем URL для загрузки
 *     const response = await getFileUrl(opId, taskId, fileData);
 *     
 *     // Загружаем файл
 *     await uploadFile(response.data.url, file);
 *     
 *     // Подтверждаем загрузку
 *     await approveDoerPhotos(userId, taskId, [url]);
 *   };
 * }
 * ```
 * 
 * @see {@link api} для оригинальных API функций
 * @see {@link useQueryParams} для получения URL параметров
 */
export const useCameraRequest = () => {
    const params = useQueryParams();
    
    const min = useMemo(() => Number(params?.min_size || 1), [params?.min_size]);
    const max = useMemo(() => Number(params?.max_size || 10), [params?.max_size]);

    const getFileUrl = useCallback(async (operationId: string, taskElementId: string, payload: GetFileUrlPayload) => {
        return api.getFileUrl(operationId, taskElementId, payload);
    }, []);

    const uploadFile = useCallback(async (url: string, file: File) => {
        return api.uploadFile(url, file);
    }, []);

    const approveDoerPhotos = useCallback(async (doerUserId: string, taskElementId: string, urls: string[]) => {
        return api.approveDoerPhotos(doerUserId, taskElementId, urls);
    }, []);

    return useMemo(() => ({
        min,
        max,
        getFileUrl,
        uploadFile,
        approveDoerPhotos
    }), [min, max, getFileUrl, uploadFile, approveDoerPhotos]);
};
